import { useEffect, useState } from 'react';

import { Send, X } from 'lucide-react';

import { Button } from '@/_components';
import { RichText } from '@/_containers/inputs/rich-text';

import { TEXT_EDITOR_HEIGHT } from './settings';

import type { NoteMutationSchema, NoteUpdateSchema } from '../schemas/notes-mutation-schema';
import type { NoteContextTypeSchema, NoteQuerySchema } from '../schemas/notes-query-schema';

type NotesFormProps = {
  onSubmit: (data: NoteMutationSchema) => Promise<void>;
  onUpdate?: (data: NoteUpdateSchema) => Promise<void>;
  onCancelEdit?: () => void;
  contextType?: NoteContextTypeSchema;
  contextId?: string;
  editNote?: NoteQuerySchema | null;
  isEditMode?: boolean;
  height?: number;
  onFocus?: (isFocused: boolean) => void;
};

export function NotesForm({
  onSubmit: onNoteSubmit,
  onUpdate,
  onCancelEdit,
  contextType = 'client',
  contextId,
  editNote,
  isEditMode = false,
  height = TEXT_EDITOR_HEIGHT,
  onFocus,
}: NotesFormProps) {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && editNote) {
      // Switching to edit mode - populate with existing note message
      setMessage(editNote.message);
    } else if (!isEditMode) {
      // Switching to create mode - clear the message
      setMessage('');
    }
  }, [isEditMode, editNote]);

  const handleSubmit = async () => {
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);

    if (isEditMode && editNote && onUpdate) {
      const payload: NoteUpdateSchema = {
        id: editNote.id,
        message: message.trim(),
      };
      await onUpdate(payload);
    } else {
      const payload: NoteMutationSchema = {
        context_id: parseInt(contextId || '0'),
        context_type: contextType,
        message: message.trim(),
      };
      await onNoteSubmit(payload);
      setMessage(''); // Clear message after successful submission in create mode
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
    setMessage('');
    setIsSubmitting(false);
  };

  return (
    <div className="flex w-full items-end gap-3">
      <div className="flex-1">
        <RichText
          label=""
          value={message}
          onChange={setMessage}
          placeholder={isEditMode ? 'Edit your note...' : 'Type your note...'}
          height={height}
          inline={false}
          messengerMode
          className="h-full"
          disabled={isSubmitting}
          onFocus={onFocus}
          autoResize
          min_height={TEXT_EDITOR_HEIGHT}
          max_height={TEXT_EDITOR_HEIGHT * 4.5}
        />
      </div>

      {/* Action buttons */}
      <div className="flex h-12 gap-2 py-1">
        {isEditMode && (
          <Button
            onClick={handleCancel}
            as="outline"
            className="h-10 w-10 shrink-0 rounded-full p-0"
            size="sm"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !message.trim()}
          className="h-10 w-10 shrink-0 rounded-full p-0 pr-0.5"
          iconClassName="size-5"
          size="sm"
          showLoader={isSubmitting}
          icon={Send}
        />
      </div>
    </div>
  );
}
